/**
 * ProcurementPipeline — PO-centric unified view of the inbound flow.
 *
 * Industry standard pattern (NetSuite WMS, Cin7 Core, Odoo Inventory):
 * each Purchase Order is a single expandable row that owns its entire
 * lifecycle — from Draft → Approved → In Transit → Received → Done.
 * Linked Incoming Delivery (DO) and activity log are nested under the PO.
 *
 * Status tabs across the top filter by lifecycle stage. Each row collapses
 * to a summary and expands inline to reveal:
 *   ① Stage timeline (visual progress dots)
 *   ② Nested Incoming Delivery + receive/cancel actions
 *   ③ Activity log
 */
import React, { useState, useMemo } from 'react';
import {
  ChevronRight, ChevronDown, FileText, Truck, CheckCircle, AlertTriangle,
  Clock, Plus, Search, Edit2, X
} from 'lucide-react';

// ─── Stage definitions ────────────────────────────────────────────────────────
const STAGES = [
  { id: 'all',        label: 'All',         icon: FileText,      color: 'gray'    },
  { id: 'draft',      label: 'Drafts',      icon: Edit2,         color: 'amber'   },
  { id: 'approved',   label: 'Approved',    icon: CheckCircle,   color: 'emerald' },
  { id: 'in_transit', label: 'In Transit',  icon: Truck,         color: 'blue'    },
  { id: 'received',   label: 'Received',    icon: CheckCircle,   color: 'emerald' },
  { id: 'cancelled',  label: 'Cancelled',   icon: X,             color: 'gray'    },
];

const STAGE_BADGE = {
  draft:      'bg-amber-100 text-amber-800 border-amber-200',
  approved:   'bg-blue-100 text-blue-800 border-blue-200',
  in_transit: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  received:   'bg-emerald-100 text-emerald-800 border-emerald-200',
  cancelled:  'bg-gray-100 text-gray-600 border-gray-200',
  rejected:   'bg-gray-100 text-gray-600 border-gray-200',
};

// ─── Pipeline computation ─────────────────────────────────────────────────────

/**
 * Join drafts with their deliveries and compute a unified lifecycle stage.
 * Cancelled deliveries that came from approved POs surface as cancelled.
 */
function buildPipeline(drafts, allDeliveries) {
  const byDraft = {};
  (allDeliveries || []).forEach(dl => {
    if (!dl.draft_id) return;
    if (!byDraft[dl.draft_id]) byDraft[dl.draft_id] = [];
    byDraft[dl.draft_id].push(dl);
  });

  return (drafts || []).map(d => {
    const deliveries = byDraft[d.draft_id] || [];
    const active = deliveries.find(x => x.status === 'pending')
                || deliveries.find(x => x.status === 'confirmed')
                || deliveries[0];

    let stage;
    if (d.status === 'rejected')                    stage = 'rejected';
    else if (d.status === 'pending')                stage = 'draft';
    else if (d.status === 'cancelled')              stage = 'cancelled';
    else if (active && active.status === 'cancelled') stage = 'cancelled';
    else if (active && active.status === 'confirmed') stage = 'received';
    else if (active && active.status === 'pending')   stage = 'in_transit';
    else                                              stage = 'approved';

    return { ...d, stage, deliveries, activeDelivery: active };
  });
}

// ─── Visual stage timeline (5 dots) ───────────────────────────────────────────

const STAGE_ORDER = ['draft', 'approved', 'in_transit', 'received', 'done'];
const STAGE_ORDER_LABEL = { draft: 'Draft', approved: 'Approved', in_transit: 'In Transit', received: 'Received', done: 'Done' };

function StageTimeline({ row }) {
  const reachedIndex = (() => {
    if (row.stage === 'cancelled' || row.stage === 'rejected') return -1;
    if (row.stage === 'received') return 4;
    if (row.stage === 'in_transit') return 2;
    if (row.stage === 'approved') return 1;
    return 0;
  })();

  if (row.stage === 'cancelled' || row.stage === 'rejected') {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <X className="w-3.5 h-3.5 text-gray-400" />
        {row.stage === 'rejected' ? 'PO rejected' : 'Cancelled'}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 text-[11px]">
      {STAGE_ORDER.map((s, i) => {
        const reached = i <= reachedIndex;
        const current = i === reachedIndex;
        return (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center min-w-[60px]">
              <div className={`w-3 h-3 rounded-full border-2 transition-colors ${
                reached
                  ? current ? 'bg-blue-500 border-blue-500 ring-4 ring-blue-100' : 'bg-emerald-500 border-emerald-500'
                  : 'bg-white border-gray-300'
              }`} />
              <span className={`mt-1 ${reached ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
                {STAGE_ORDER_LABEL[s]}
              </span>
            </div>
            {i < STAGE_ORDER.length - 1 && (
              <div className={`flex-1 h-[2px] -mt-3.5 ${i < reachedIndex ? 'bg-emerald-500' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Activity log derived from PO/delivery fields ─────────────────────────────

function buildActivity(row) {
  const items = [];
  if (row.created_at) items.push({ when: row.created_at, what: 'Draft created', who: 'Agent (low stock)' });
  if (row.approved_at) items.push({ when: row.approved_at, what: 'Approved & order placed', who: row.approved_by });
  if (row.po_sent_at)  items.push({ when: row.po_sent_at,  what: `Sent to supplier${row.po_sent_to ? ` (${row.po_sent_to})` : ''}`, who: null });
  (row.deliveries || []).forEach(dl => {
    if (dl.created_at) items.push({ when: dl.created_at, what: `Delivery ${dl.delivery_id} expected ${dl.expected_date || ''}`, who: null });
    if (dl.confirmed_at) items.push({ when: dl.confirmed_at, what: `Receipt confirmed (${dl.actual_quantity || dl.quantity} units)`, who: dl.confirmed_by });
  });
  return items.sort((a, b) => new Date(a.when) - new Date(b.when));
}

// ─── Single PO row (expandable) ───────────────────────────────────────────────

function POrow({
  row, cur, API, expanded, onToggle,
  // draft actions
  poActionLoading, editingPO, setEditingPO, poEdits, setPoEdits,
  approveDraftPO, rejectDraftPO, suppliersList, setSuppliersList,
  // processed actions
  sendingPO, setSendingPO, sendEmail, setSendEmail, sendMsg, handleSendPO,
  setCancelModal, restorePO, restoreDO,
  // delivery actions
  setConfirmModal,
}) {
  const isDraft = row.stage === 'draft';
  const qty       = row.final_qty || row.suggested_qty;
  const unitCost  = row.final_unit_cost || row.estimated_unit_cost;
  const supplier  = row.suggested_supplier;
  const total     = (row.final_qty && row.final_unit_cost ? row.final_qty * row.final_unit_cost : row.estimated_total) || (qty * unitCost);
  const dl        = row.activeDelivery;
  const activity  = useMemo(() => buildActivity(row), [row]);

  const stageBadge = STAGE_BADGE[row.stage] || STAGE_BADGE.draft;
  const stageLabel = row.stage.replace('_', ' ').toUpperCase();

  const headerTone =
    row.stage === 'cancelled' || row.stage === 'rejected'
      ? 'bg-gray-50 hover:bg-gray-100/60'
      : row.stage === 'received'
      ? 'bg-emerald-50/40 hover:bg-emerald-50/70'
      : row.stage === 'in_transit'
      ? 'bg-indigo-50/40 hover:bg-indigo-50/70'
      : row.stage === 'approved'
      ? 'bg-blue-50/40 hover:bg-blue-50/70'
      : 'bg-amber-50/40 hover:bg-amber-50/70';

  const dimmed = row.stage === 'cancelled' || row.stage === 'rejected';

  return (
    <div id={`po-${row.draft_id}`} className="border rounded-xl overflow-hidden bg-white">
      {/* Collapsed/header row */}
      <button
        onClick={onToggle}
        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${headerTone}`}
        data-testid={`po-row-${row.draft_id}`}
      >
        {expanded ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-semibold text-sm ${dimmed ? 'line-through text-gray-400' : 'text-gray-900'}`}>{row.product_name}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase border ${stageBadge}`}>{stageLabel}</span>
            {dl?.is_overdue && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-red-200 text-red-800">OVERDUE</span>}
            {dl?.has_discrepancy && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-amber-200 text-amber-800">DISCREPANCY</span>}
          </div>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {qty} units · {supplier} · {cur}{Number(total)?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            {row.purchase_id && <span className="text-gray-400 font-mono ml-2">{row.purchase_id}</span>}
          </p>
        </div>
        {!expanded && (
          <div className="hidden md:flex items-center gap-1.5 text-[10px] text-gray-400 shrink-0">
            {row.approved_at && <span>{new Date(row.approved_at).toLocaleDateString()}</span>}
            {dl?.expected_date && <span>· ETA {dl.expected_date}</span>}
          </div>
        )}
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="border-t bg-gray-50/40 px-5 py-4 space-y-5">
          {/* Stage timeline */}
          <div><StageTimeline row={row} /></div>

          {/* PO summary + draft edit form */}
          <DraftBlock
            row={row} cur={cur} API={API}
            editingPO={editingPO} setEditingPO={setEditingPO}
            poEdits={poEdits} setPoEdits={setPoEdits}
            poActionLoading={poActionLoading}
            approveDraftPO={approveDraftPO} rejectDraftPO={rejectDraftPO}
            suppliersList={suppliersList} setSuppliersList={setSuppliersList}
          />

          {/* Action buttons (stage-aware) */}
          <ActionBar
            row={row} cur={cur} API={API}
            isDraft={isDraft} dl={dl}
            sendingPO={sendingPO} setSendingPO={setSendingPO}
            sendEmail={sendEmail} setSendEmail={setSendEmail}
            sendMsg={sendMsg} handleSendPO={handleSendPO}
            setCancelModal={setCancelModal}
            restorePO={restorePO} restoreDO={restoreDO}
            setConfirmModal={setConfirmModal}
          />

          {/* Linked delivery */}
          {dl && <DeliveryCard dl={dl} cur={cur} setConfirmModal={setConfirmModal} setCancelModal={setCancelModal} />}

          {/* Activity */}
          {activity.length > 0 && <ActivityLog items={activity} />}
        </div>
      )}
    </div>
  );
}

// ─── Inline draft edit form ───────────────────────────────────────────────────

function DraftBlock({
  row, cur, API,
  editingPO, setEditingPO, poEdits, setPoEdits, poActionLoading,
  approveDraftPO, rejectDraftPO, suppliersList, setSuppliersList,
}) {
  if (row.stage !== 'draft') return null;
  const isEditing = editingPO === row.draft_id;
  const edits = poEdits[row.draft_id] || {};
  const qty = edits.qty ?? row.suggested_qty;
  const uc  = edits.unitCost ?? row.estimated_unit_cost;
  const sup = edits.supplier ?? row.suggested_supplier;
  const tot = qty * uc;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${row.risk_level === 'critical' ? 'bg-red-200 text-red-800' : row.risk_level === 'high' ? 'bg-orange-200 text-orange-800' : 'bg-amber-200 text-amber-800'}`}>{row.risk_level}</span>
            <span className="text-xs text-gray-600">{row.current_stock} left · {row.days_until_stockout}d until stockout</span>
          </div>
          {!isEditing && (
            <p className="text-sm text-gray-700">
              Order <strong>{qty}</strong> units from <strong>{sup}</strong> · Total <strong>{cur}{tot?.toLocaleString()}</strong>
            </p>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          {!isEditing && (
            <button
              onClick={async () => {
                setEditingPO(row.draft_id);
                setPoEdits(p => ({ ...p, [row.draft_id]: { qty: row.suggested_qty, unitCost: row.estimated_unit_cost, supplier: row.suggested_supplier } }));
                if (!suppliersList?.length) {
                  try {
                    const axios = (await import('axios')).default;
                    const r = await axios.get(`${API}/suppliers`);
                    setSuppliersList(r.data);
                  } catch { /* ignore */ }
                }
              }}
              className="text-xs border border-blue-200 text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-50 font-medium"
              data-testid={`edit-po-${row.draft_id}`}>Edit</button>
          )}
          <button onClick={() => rejectDraftPO(row.draft_id, 'Not needed')}
            disabled={!!poActionLoading}
            className="text-xs bg-red-100 text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-200 font-medium disabled:opacity-50">Reject</button>
          <button onClick={() => approveDraftPO(row.draft_id, qty, uc)}
            disabled={!!poActionLoading}
            className="text-xs bg-emerald-500 text-white px-4 py-1.5 rounded-lg hover:bg-emerald-600 font-medium shadow-sm disabled:opacity-50"
            data-testid={`approve-po-${row.draft_id}`}>Approve</button>
        </div>
      </div>

      {isEditing && (
        <div className="mt-3 grid grid-cols-3 gap-3 bg-white rounded-lg p-3 border">
          <div>
            <label className="text-[10px] font-semibold text-gray-500 uppercase">Quantity</label>
            <input type="number" min="1" value={qty}
              onChange={e => setPoEdits(p => ({ ...p, [row.draft_id]: { ...p[row.draft_id], qty: Math.max(1, parseInt(e.target.value) || 1) } }))}
              className="w-full mt-1 text-sm border rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-300 outline-none"
              data-testid={`po-qty-${row.draft_id}`} />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-gray-500 uppercase">Unit Cost ({cur})</label>
            <input type="number" min="0" step="0.01" value={uc}
              onChange={e => setPoEdits(p => ({ ...p, [row.draft_id]: { ...p[row.draft_id], unitCost: Math.max(0, parseFloat(e.target.value) || 0) } }))}
              className="w-full mt-1 text-sm border rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-300 outline-none"
              data-testid={`po-cost-${row.draft_id}`} />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-gray-500 uppercase">Supplier</label>
            <select value={sup}
              onChange={e => setPoEdits(p => ({ ...p, [row.draft_id]: { ...p[row.draft_id], supplier: e.target.value } }))}
              className="w-full mt-1 text-sm border rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-300 outline-none bg-white"
              data-testid={`po-supplier-${row.draft_id}`}>
              {sup && <option value={sup}>{sup}</option>}
              {(suppliersList || []).filter(s => s.name !== sup).map(s => (
                <option key={s.supplier_id} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className="col-span-3 flex items-center justify-between text-xs text-gray-500">
            <span>Total: <strong className="text-gray-900">{cur}{tot?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
            <button onClick={() => setEditingPO(null)} className="text-gray-500 hover:text-gray-800 px-3 py-1 rounded-lg hover:bg-gray-100">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stage-aware action bar ───────────────────────────────────────────────────

function ActionBar({
  row, cur, API, isDraft, dl,
  sendingPO, setSendingPO, sendEmail, setSendEmail, sendMsg, handleSendPO,
  setCancelModal, restorePO, restoreDO, setConfirmModal,
}) {
  if (isDraft) return null;
  const showSend = (row.stage === 'approved' || row.stage === 'in_transit') && !dl?.confirmed_at;
  const showCancelPO = row.stage === 'approved' || row.stage === 'in_transit';
  const showRestorePO = row.stage === 'cancelled' || row.stage === 'rejected';

  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        {row.po_sent_to && <span className="text-emerald-700 font-medium">✓ Sent to {row.po_sent_to}</span>}
        {sendMsg?.[row.draft_id]?.type === 'error' && <span className="text-red-600 font-medium">{sendMsg[row.draft_id].text}</span>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {showSend && sendingPO === row.draft_id ? (
          <div className="flex items-center gap-1.5">
            <input type="email" placeholder={row.po_sent_to || row.supplier_email || 'Supplier email'}
              value={sendEmail} onChange={e => setSendEmail(e.target.value)}
              className="text-xs border rounded-lg px-2 py-1.5 w-48 focus:ring-2 focus:ring-blue-300 outline-none"
              data-testid={`po-send-email-${row.draft_id}`} />
            <button onClick={() => handleSendPO(row.draft_id)}
              disabled={sendMsg?.[row.draft_id]?.type === 'loading'}
              className="text-xs bg-blue-500 text-white px-3 py-1.5 rounded-lg hover:bg-blue-600 font-medium disabled:opacity-50">
              {sendMsg?.[row.draft_id]?.type === 'loading' ? 'Sending…' : 'Send'}
            </button>
            <button onClick={() => { setSendingPO(null); setSendEmail(''); }}
              className="text-xs text-gray-400 hover:text-gray-700 px-1">✕</button>
          </div>
        ) : showSend ? (
          <button onClick={() => { setSendingPO(row.draft_id); setSendEmail(row.po_sent_to || row.supplier_email || ''); }}
            className="text-xs border border-blue-200 text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-50 font-medium"
            data-testid={`send-po-${row.draft_id}`}>
            {row.po_sent_at ? 'Resend PO' : 'Send to Supplier'}
          </button>
        ) : null}

        <button onClick={() => window.open(`${API}/docs/po/${row.draft_id}/pdf`, '_blank')}
          className="text-xs border px-3 py-1.5 rounded-lg hover:bg-white">PDF</button>

        {showCancelPO && (
          <button onClick={() => setCancelModal({ type: 'po', id: row.draft_id, name: row.product_name })}
            className="text-xs bg-orange-100 text-orange-700 px-3 py-1.5 rounded-lg hover:bg-orange-200 font-medium"
            data-testid={`cancel-po-${row.draft_id}`}>Cancel PO</button>
        )}
        {showRestorePO && (
          <button onClick={() => restorePO(row.draft_id)}
            className="text-xs bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-200 font-medium"
            data-testid={`restore-po-${row.draft_id}`}>Restore PO</button>
        )}
      </div>
    </div>
  );
}

// ─── Nested delivery card ─────────────────────────────────────────────────────

function DeliveryCard({ dl, cur, setConfirmModal, setCancelModal }) {
  const tone =
    dl.status === 'cancelled' ? 'border-gray-200 bg-gray-50 opacity-75' :
    dl.status === 'confirmed' ? (dl.has_discrepancy ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50') :
    dl.is_overdue ? 'border-red-200 bg-red-50' :
    'border-blue-200 bg-blue-50';

  return (
    <div className={`rounded-lg border ${tone} p-4`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Truck className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Incoming Delivery</span>
            <span className="text-[10px] font-mono text-gray-400">{dl.delivery_id}</span>
          </div>
          {dl.status === 'pending' && (
            <p className="text-sm text-gray-700">
              <strong>{dl.quantity} units</strong> · {cur}{dl.total_cost?.toLocaleString()} · ETA {dl.expected_date}
            </p>
          )}
          {dl.status === 'confirmed' && (
            <p className="text-sm text-gray-700">
              Received <strong>{dl.actual_quantity || dl.quantity} units</strong>
              {dl.actual_unit_cost ? ` @ ${cur}${dl.actual_unit_cost}` : ''}
              {dl.confirmed_at && <span className="text-gray-400"> · {new Date(dl.confirmed_at).toLocaleString()}</span>}
              {dl.confirmed_by && <span className="text-gray-400"> · by {dl.confirmed_by}</span>}
            </p>
          )}
          {dl.status === 'cancelled' && (
            <p className="text-sm text-gray-500">{dl.quantity} units · cancelled</p>
          )}
          {dl.has_discrepancy && dl.discrepancy_reason && (
            <p className="text-[11px] text-amber-700 mt-1">⚠ Discrepancy: {dl.discrepancy_reason.replace(/_/g, ' ')}</p>
          )}
        </div>
        {dl.status === 'pending' && (
          <div className="flex gap-1.5 shrink-0">
            <button onClick={() => setConfirmModal(dl)}
              className="text-xs bg-emerald-500 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-600 font-medium shadow-sm flex items-center gap-1"
              data-testid={`confirm-do-${dl.delivery_id}`}>
              <CheckCircle className="w-3.5 h-3.5" />Confirm Receipt
            </button>
            <button onClick={() => setCancelModal({ type: 'do', id: dl.delivery_id, name: dl.product_name })}
              className="text-xs bg-orange-100 text-orange-700 px-3 py-1.5 rounded-lg hover:bg-orange-200 font-medium"
              data-testid={`cancel-do-${dl.delivery_id}`}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Activity log ─────────────────────────────────────────────────────────────

function ActivityLog({ items }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
        <Clock className="w-3.5 h-3.5" />Activity
      </div>
      <ul className="space-y-1.5 text-xs">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0" />
            <div className="flex-1">
              <span className="text-gray-700">{it.what}</span>
              {it.who && <span className="text-gray-400"> · {it.who}</span>}
              <span className="text-gray-400 ml-2">{new Date(it.when).toLocaleString()}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Main exported component ──────────────────────────────────────────────────

export default function ProcurementPipeline(props) {
  const {
    drafts, deliveries, cur, API, openCreatePO,
  } = props;

  const [activeTab, setActiveTab]   = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch]         = useState('');

  const pipeline = useMemo(() => buildPipeline(drafts, deliveries), [drafts, deliveries]);

  const counts = useMemo(() => {
    const c = { all: pipeline.length };
    pipeline.forEach(r => {
      const key = r.stage === 'rejected' ? 'cancelled' : r.stage;
      c[key] = (c[key] || 0) + 1;
    });
    return c;
  }, [pipeline]);

  const visible = useMemo(() => {
    let list = pipeline;
    if (activeTab !== 'all') {
      list = list.filter(r => activeTab === 'cancelled'
        ? (r.stage === 'cancelled' || r.stage === 'rejected')
        : r.stage === activeTab);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(r =>
        (r.product_name || '').toLowerCase().includes(q)
        || (r.purchase_id || '').toLowerCase().includes(q)
        || (r.suggested_supplier || '').toLowerCase().includes(q)
        || (r.activeDelivery?.delivery_id || '').toLowerCase().includes(q));
    }
    // sort: drafts first by created, others by approved_at desc
    return [...list].sort((a, b) => {
      const ta = new Date(a.approved_at || a.created_at || 0).getTime();
      const tb = new Date(b.approved_at || b.created_at || 0).getTime();
      return tb - ta;
    });
  }, [pipeline, activeTab, search]);

  return (
    <div className="px-5 py-4">
      {/* Header: search + create */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search PO, product, supplier, delivery…"
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none"
            data-testid="procurement-search"
          />
        </div>
        <button onClick={openCreatePO}
          className="text-xs bg-blue-500 text-white px-3 py-2 rounded-lg hover:bg-blue-600 font-medium flex items-center gap-1"
          data-testid="create-po-btn">
          <Plus className="w-3.5 h-3.5" />Create PO
        </button>
      </div>

      {/* Stage tabs */}
      <div className="flex gap-1.5 border-b mb-4 overflow-x-auto">
        {STAGES.map(st => {
          const active = activeTab === st.id;
          const Icon = st.icon;
          const count = counts[st.id] || 0;
          return (
            <button
              key={st.id}
              onClick={() => setActiveTab(st.id)}
              className={`relative px-3 py-2 text-xs font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                active
                  ? 'text-blue-700 border-b-2 border-blue-500 -mb-px'
                  : 'text-gray-500 hover:text-gray-800 border-b-2 border-transparent'
              }`}
              data-testid={`pipeline-tab-${st.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {st.label}
              {count > 0 && (
                <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                  active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Rows */}
      {visible.length === 0 ? (
        <div className="flex items-center gap-3 px-5 py-8 text-gray-400 justify-center">
          <AlertTriangle className="w-5 h-5" />
          <span className="text-sm">No purchase orders in this stage.</span>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map(row => (
            <POrow
              key={row.draft_id}
              row={row}
              cur={cur}
              API={API}
              expanded={expandedId === row.draft_id}
              onToggle={() => setExpandedId(expandedId === row.draft_id ? null : row.draft_id)}
              {...props}
            />
          ))}
        </div>
      )}
    </div>
  );
}
